import * as s from "pages/recorders/RecordersStyled";
import { convertType } from "utils/functions";
import BigButton from "components/atoms/buttons/BigButton";
import Type from "components/atoms/text/labels/Type";
import {
  FirmwareUpdateInfoResult,
  FIRMWARE_UPDATE_STEP,
} from "api/interfaces/settingsInterface.interface";
import { RELEASE_NOTE_TYPE } from "utils/firmwareUtil";
import Spinner from "components/atoms/loader/Spinner";
import RBACWrapper from "components/blocks/function/RBACWrapper";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import { useMemo } from "react";
import {
  getAccountTimeFormat,
  getDisableUpdateTime,
  getFirmwareReserveSchedule,
} from "utils/timeUtil";

export default function FirmwareIntro({
  data,
  autoUpdate,
  onClickManageAutoUpdate,
  onClickUpdate,
  onClickCancel,
  onModal,
  isLoadingUpdate,
  disabledUpdate,
}: {
  data: FirmwareUpdateInfoResult;
  onClickManageAutoUpdate: () => void;
  onClickUpdate: () => void;
  onClickCancel: () => void;
  onModal: (
    type: typeof RELEASE_NOTE_TYPE[keyof typeof RELEASE_NOTE_TYPE]
  ) => void;
  isLoadingUpdate: boolean;
  disabledUpdate: boolean;
  autoUpdate: boolean;
}) {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const scheduledInfo = useMemo(() => {
    if (!autoUpdate || !data?.autoUpgradeTime) {
      return {
        scheduleDate: null,
        disableUpdate: false,
      };
    }
    const scheduleDate = getFirmwareReserveSchedule(
      data?.autoUpgradeTime,
      selectedAccount
    );
    return {
      scheduleDate: scheduleDate,
      disableUpdate: getDisableUpdateTime(scheduleDate),
    };
  }, [autoUpdate, data, selectedAccount]);
  return (
    <s.UpdateFirmwareStyled.Wrapper>
      <s.UpdateFirmwareStyled.Text>
        {autoUpdate
          ? "This recorder will automatically update to the latest firmware version."
          : "Would you like to update this recorder to the latest firmware version?"}
        {scheduledInfo.scheduleDate && (
          <span className="scheduled-time">
            The update will be performed at{" "}
            {getAccountTimeFormat(
              scheduledInfo.scheduleDate,
              selectedAccount,
              true
            )}
            .
          </span>
        )}
      </s.UpdateFirmwareStyled.Text>
      <s.UpdateFirmwareStyled.Settings>
        <RBACWrapper requiredPermissions="manageMaintenance">
          <span
            onClick={onClickManageAutoUpdate}
            data-testid="recorders-edit-step-span-click-manage-auto-update"
          >
            Manage Auto-Update
          </span>
        </RBACWrapper>
      </s.UpdateFirmwareStyled.Settings>
      <s.UpdateFirmwareStyled.RecorderInfo.Wrapper>
        <s.UpdateFirmwareStyled.RecorderInfo.Label>
          <Type
            type={convertType({
              type: data.recorderType,
              displayType: data.displayType,
            })}
          />

          <span>{data.mergedSystemName}</span>
        </s.UpdateFirmwareStyled.RecorderInfo.Label>
        <s.UpdateFirmwareStyled.RecorderInfo.Cameras>
          Cameras: {data.cameraCount}
        </s.UpdateFirmwareStyled.RecorderInfo.Cameras>
      </s.UpdateFirmwareStyled.RecorderInfo.Wrapper>
      <s.UpdateFirmwareStyled.Version.Wrapper>
        <s.UpdateFirmwareStyled.Version.Label
          status={FIRMWARE_UPDATE_STEP.INTRO}
        >
          <span>Current Version</span>
          <b>Version {data.currentVersion}</b>
        </s.UpdateFirmwareStyled.Version.Label>
        {data.currentReleaseNote && (
          <s.UpdateFirmwareStyled.Version.ReleaseNote
            onClick={() => onModal(RELEASE_NOTE_TYPE.CURRENT)}
            data-testid="recorders-edit-step-release-note-modal"
          >
            View Release Note
          </s.UpdateFirmwareStyled.Version.ReleaseNote>
        )}
      </s.UpdateFirmwareStyled.Version.Wrapper>
      <s.UpdateFirmwareStyled.Version.Wrapper>
        <s.UpdateFirmwareStyled.Version.Label
          status={FIRMWARE_UPDATE_STEP.SUCCESS}
        >
          <span>Update Version [Recommended]</span>
          <b>Version {data.updateVersion}</b>
        </s.UpdateFirmwareStyled.Version.Label>
        {data.updateReleaseNote && (
          <s.UpdateFirmwareStyled.Version.ReleaseNote
            onClick={() => onModal(RELEASE_NOTE_TYPE.UPDATE)}
            data-testid="recorders-edit-step-release-note-modal-2"
          >
            View Release Note
          </s.UpdateFirmwareStyled.Version.ReleaseNote>
        )}
      </s.UpdateFirmwareStyled.Version.Wrapper>
      {isLoadingUpdate && <Spinner />}
      <s.UpdateFirmwareStyled.Buttons>
        <BigButton
          onClickBtn={onClickCancel}
          label="Cancel"
          buttonType="cancel"
          data-testid="recorders-edit-step-button-click-cancel"
        />

        <BigButton
          onClickBtn={onClickUpdate}
          label="Update"
          disabled={disabledUpdate || scheduledInfo.disableUpdate}
            data-testid="recorders-edit-step-button-click-update"
        />
      </s.UpdateFirmwareStyled.Buttons>
    </s.UpdateFirmwareStyled.Wrapper>
  );
}
